import React, { useState } from 'react';
import { useAudioPlayer } from './useAudioPlayer';
import { Button, IconButton, cn } from '../ui';
import type { PlaybackRate, RepeatMode } from './types';

function fmt(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}

const RATES: PlaybackRate[] = [0.75, 1.0, 1.25, 1.5];
const REPEAT_CYCLE: RepeatMode[] = ['off', 'one', 'pallavi'];

export interface AudioDockProps {
  variant?: 'default' | 'distraction';
}

export const AudioDock: React.FC<AudioDockProps> = ({ variant = 'default' }) => {
  const { state, play, toggle, seek, next, previous, skipForward, skipBackward, setPlaybackRate, setRepeatMode } = useAudioPlayer();
  const [rateMenuOpen, setRateMenuOpen] = useState(false);

  if (!state.currentTrack) return null;

  const t = state.currentTrack;
  const repeatIdx = REPEAT_CYCLE.indexOf(state.repeatMode);
  const nextRepeat = REPEAT_CYCLE[(repeatIdx + 1) % REPEAT_CYCLE.length];

  return (
    <div
      role="region"
      aria-label="Audio player"
      className={cn(
        'fixed bottom-0 left-0 right-0 z-[900] border-t border-light',
        variant === 'distraction' && 'transition-opacity duration-400'
      )}
      style={{ background: 'var(--glass-bg)', backdropFilter: 'var(--glass-blur)' }}
    >
      <div className="mx-auto max-w-7xl flex items-center gap-3 px-3 sm:px-4 py-2">
        {/* Cover */}
        <div className="h-14 w-14 shrink-0 rounded-md bg-bg-3 overflow-hidden">
          {t.artworkUrl ? (
            <img src={t.artworkUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-accent-gold/30 to-accent-indigo/30" aria-hidden />
          )}
        </div>

        {/* Title + scrubber */}
        <div className="flex-1 min-w-0">
          <div className="truncate text-small font-semibold text-text-primary">{t.title}</div>
          <div className="truncate text-[11px] text-text-tertiary">{t.artist}</div>
          <div className="mt-1 flex items-center gap-2">
            <input
              type="range"
              min={0}
              max={Math.max(state.durationMs, 1)}
              value={state.currentTimeMs}
              onChange={(e) => seek(parseInt(e.target.value, 10))}
              aria-label="Seek"
              className="flex-1 h-1 bg-bg-4 rounded-full accent-accent-indigo"
            />
            <span className="font-mono text-[11px] text-text-tertiary tabular-nums w-[88px] text-right">
              {fmt(state.currentTimeMs)} / {fmt(state.durationMs)}
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1 sm:gap-2">
          <IconButton ariaLabel="Previous" variant="ghost" size="sm" onClick={previous} icon={<TriIcon dir="left-double" />} />
          <IconButton ariaLabel="Back 10 seconds" variant="ghost" size="sm" onClick={() => skipBackward(10_000)} icon={<span className="text-[10px] font-bold">-10</span>} />
          <button
            type="button"
            onClick={toggle}
            aria-pressed={state.isPlaying}
            aria-label={state.isPlaying ? 'Pause' : 'Play'}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-emerald text-bg-0 hover:brightness-110 active:brightness-95 shadow-2"
          >
            {state.isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <IconButton ariaLabel="Forward 10 seconds" variant="ghost" size="sm" onClick={() => skipForward(10_000)} icon={<span className="text-[10px] font-bold">+10</span>} />
          <IconButton ariaLabel="Next" variant="ghost" size="sm" onClick={next} icon={<TriIcon dir="right-double" />} />

          {/* Rate */}
          <div className="relative hidden sm:block">
            <button
              type="button"
              onClick={() => setRateMenuOpen((v) => !v)}
              className="px-2 py-1 text-small font-mono text-text-secondary hover:text-text-primary"
              aria-haspopup="menu"
              aria-expanded={rateMenuOpen}
            >
              {state.playbackRate}×
            </button>
            {rateMenuOpen && (
              <div role="menu" className="absolute bottom-full right-0 mb-2 rounded-md border border-light bg-bg-2 shadow-3 py-1 min-w-[80px]">
                {RATES.map((r) => (
                  <button
                    key={r}
                    role="menuitemradio"
                    aria-checked={state.playbackRate === r}
                    onClick={() => { setPlaybackRate(r); setRateMenuOpen(false); }}
                    className={cn(
                      'block w-full text-center px-3 py-1 text-small font-mono',
                      state.playbackRate === r ? 'text-accent-gold' : 'text-text-secondary hover:bg-bg-3'
                    )}
                  >
                    {r}×
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Repeat */}
          <IconButton
            ariaLabel={`Repeat: ${state.repeatMode}`}
            variant="ghost"
            size="sm"
            onClick={() => setRepeatMode(nextRepeat)}
            icon={
              <span className={cn('text-[10px] font-bold', state.repeatMode !== 'off' && 'text-accent-gold')}>
                {state.repeatMode === 'pallavi' ? 'P' : state.repeatMode === 'one' ? '1' : '↻'}
              </span>
            }
          />
        </div>
      </div>

      {state.error && (
        <div className="border-t border-accent-rose/30 bg-accent-rose/10 px-4 py-1.5 text-small text-accent-rose">
          {state.error}{' '}
          {state.error?.startsWith('Tap to start') && (
            <Button size="sm" variant="secondary" onClick={() => play()}>Retry</Button>
          )}
        </div>
      )}
    </div>
  );
};

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
);
const PauseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden><path d="M6 5h4v14H6zm8 0h4v14h-4z" fill="currentColor" /></svg>
);
const TriIcon: React.FC<{ dir: 'left-double' | 'right-double' }> = ({ dir }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden style={{ transform: dir === 'left-double' ? 'scaleX(-1)' : undefined }}>
    <path d="M6 6l8 6-8 6V6zm10 0v12h2V6h-2z" fill="currentColor" />
  </svg>
);
